import { computed, ref } from "vue"
import { defineStore } from "pinia"

import { apiRequest } from "../lib/api"
import type { ApiKeyCreated, ApiKeyItem } from "../lib/types"
import { useSessionStore } from "./session"

export const useApiKeysStore = defineStore("api-keys", () => {
  const session = useSessionStore()
  const items = ref<ApiKeyItem[]>([])
  const loading = ref(false)
  const revealedKey = ref<ApiKeyCreated | null>(null)

  const activeCount = computed(() => items.value.filter((key) => key.status === "active").length)

  async function load(): Promise<void> {
    if (!session.workspace) return
    loading.value = true
    try {
      const response = await apiRequest<{ items: ApiKeyItem[] }>(
        `/v1/workspaces/${session.workspace.id}/api-keys`,
      )
      items.value = response.items
    } finally {
      loading.value = false
    }
  }

  async function create(name: string): Promise<ApiKeyCreated> {
    if (!session.workspace) throw new Error("Workspace 尚未加载")
    const created = await apiRequest<ApiKeyCreated>(
      `/v1/workspaces/${session.workspace.id}/api-keys`,
      {
        method: "POST",
        csrfToken: session.csrfToken,
        body: {
          name,
          scopes: ["content:read"],
          platforms: ["rednote", "zhihu", "tophub", "douban", "douyin", "weibo", "weibo_web", "smzdm", "bilibili", "v2ex", "xueqiu", "reddit", "twitter", "xianyu", "jd", "1688", "nowcoder", "instagram", "maimai", "jianyu", "toutiao", "sinafinance", "juejin", "36kr", "dongchedi", "ctrip", "linkedin", "github", "google", "imdb"],
        },
      },
    )
    revealedKey.value = created
    await load()
    return created
  }

  async function revoke(apiKeyId: string): Promise<void> {
    if (!session.workspace) throw new Error("Workspace 尚未加载")
    await apiRequest(`/v1/workspaces/${session.workspace.id}/api-keys/${apiKeyId}`, {
      method: "DELETE",
      csrfToken: session.csrfToken,
    })
    if (revealedKey.value?.id === apiKeyId) revealedKey.value = null
    await load()
  }

  return { items, loading, revealedKey, activeCount, load, create, revoke }
})
